import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Modal } from "react-bootstrap";
import Flatpickr from "react-flatpickr";
import "flatpickr/dist/themes/material_green.css";
import {
  getExternalEventsFilterList,
  deleteExternalEvent,
  changeExternalEventStatus,
} from "../../services/externalEventsService";
import TableHeader from "../Common/TableComponent/TableHeader";
import EntriesDropdown from "../Common/TableComponent/EntriesDropdown";
import TablesRow from "../Common/TableComponent/TablesRow";
import { Pagination } from "../Common/TableComponent/Pagination";
import TableSkeleton from "../Common/Loaders/TableSkeleton";
import Swal from "sweetalert2";
import { toast } from "react-toastify";
import { confirmDelete } from "../Common/OtherElements/confirmDeleteClone";
import { TableDataStatusError } from "../Common/OtherElements/TableDataStatusError";
import { handleErrors } from "../../utils/errorHandler";
import { usePageLevelAccess } from "../../hooks/usePageLevelAccess";
import { useUserProfile } from "../../hooks/useUserProfile";
import ComponentHeader from "../Common/OtherElements/ComponentHeader";
import allImages from "../../assets/images-import";
import { EVENT_STATUS } from "./eventConstants";
import {
  resolveEventStatus,
  renderEventStatusBadgeForItem,
} from "./eventStatusUtils";
import "./manage-events.css";

const EVENT_PUBLIC_SITE_BASE_URL = "https://www.xcruitenergy.com";

const DATE_ONLY_PICKER_OPTIONS = {
  dateFormat: "Y-m-d",
  monthSelectorType: "static",
  appendTo: typeof document !== "undefined" ? document.body : undefined,
};

const STATUS_FILTER_OPTIONS = [
  { value: "", label: "All" },
  { value: "Draft", label: "Draft" },
  { value: "Active", label: "Active" },
  { value: "Inactive", label: "Inactive" },
];

const SORT_OPTIONS = [
  { value: "default", label: "Default" },
  { value: "name_asc", label: "Event Name (A–Z)" },
  { value: "name_desc", label: "Event Name (Z–A)" },
  { value: "event_desc", label: "Newest Event Date" },
  { value: "event_asc", label: "Oldest Event Date" },
  { value: "added_desc", label: "Newest Added" },
  { value: "added_asc", label: "Oldest Added" },
];

const getEventId = (item) => item.id;

const getEventName = (item) => item.eventName || "-";

const getEventSlug = (item) => item.eventSlug || "";

const getEventLocation = (item) => {
  const value = item.eventLocation;
  return value && String(value).trim() ? String(value) : "-";
};

const getImageUrl = (item) => item.thumbImage || "";

// Existing project date formatter (day + short month + year, e.g. "10 Jun 2026").
const formatShortDate = (raw) => {
  if (!raw) return "-";
  const date = new Date(raw);
  if (Number.isNaN(date.getTime())) return String(raw);
  const day = String(date.getDate()).padStart(2, "0");
  const month = date.toLocaleString("en-US", { month: "short" });
  const year = date.getFullYear();
  return `${day} ${month} ${year}`;
};

const getEventsFromRaw = (item) => item.eventsFrom || null;
const getEventsToRaw = (item) => item.eventsTo || null;

const getAddedOnRaw = (item) => item.addedOn || null;

const getAddedOn = (item) => formatShortDate(getAddedOnRaw(item));

// https://www.xcruitenergy.com/industry-event/{eventSlug}?preview=yes
const buildEventPreviewUrl = (item) => {
  const slug = getEventSlug(item);
  const id = getEventId(item);
  if (!slug || !id) return "";
  return `${EVENT_PUBLIC_SITE_BASE_URL}/industry-event/${slug}?preview=yes`;
};

const toTimestamp = (raw) => {
  if (!raw) return null;
  const time = new Date(raw).getTime();
  return Number.isNaN(time) ? null : time;
};

// Missing dates always sort last, regardless of direction.
const compareTimestamps = (ta, tb, direction) => {
  if (ta === null && tb === null) return 0;
  if (ta === null) return 1;
  if (tb === null) return -1;
  return direction === "asc" ? ta - tb : tb - ta;
};

const renderThumbnail = (url) => (
  <img
    src={url || allImages.DefultImage}
    alt="Event"
    className="rounded xc-event-thumb"
    style={{ width: 48, height: 48, objectFit: "cover" }}
    onError={(e) => {
      e.target.src = allImages.DefultImage;
    }}
  />
);

const renderEventCell = (item) => {
  const href = buildEventPreviewUrl(item);
  const titleNode = (
    <span className="xc-event-title">{getEventName(item)}</span>
  );

  return (
    <div className="d-flex align-items-center gap-3">
      {renderThumbnail(getImageUrl(item))}
      <div className="d-flex flex-column gap-1">
        <div className="d-flex align-items-center gap-2">
          <i className="ri-links-line xc-meta-icon"></i>
          {href ? (
            <a
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              className="xc-cell-link"
            >
              {titleNode}
            </a>
          ) : (
            titleNode
          )}
        </div>
        <div className="d-flex align-items-center gap-1 xc-event-location">
          <i className="ri-map-pin-line xc-meta-icon"></i>
          <span>{getEventLocation(item)}</span>
        </div>
        <div className="d-flex align-items-center gap-1 xc-event-added-on">
          <i className="ri-calendar-event-line xc-meta-icon"></i>
          <span>Added On • {getAddedOn(item)}</span>
        </div>
      </div>
    </div>
  );
};

const renderEventDateCell = (item) => (
  <div className="d-flex flex-column xc-event-date-cell">
    <span>{formatShortDate(getEventsFromRaw(item))}</span>
    <span className="text-muted xc-event-date-arrow">
      <i className="ri-arrow-down-line"></i>
    </span>
    <span>{formatShortDate(getEventsToRaw(item))}</span>
  </div>
);

export const ManageEvents = () => {
  const [pageAccessDetails, setPageAccessDetails] = useState({});
  const pageLevelAccessUrl = "events";
  const navigate = useNavigate();
  const { pageAccessData } = usePageLevelAccess(pageLevelAccessUrl);
  const { isSuperAdmin } = useUserProfile();
  const [entriesPerPage, setEntriesPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [eventsList, setEventsList] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [statusChangingId, setStatusChangingId] = useState(null);
  const [searchInput, setSearchInput] = useState("");
  const [sortBy, setSortBy] = useState("default");
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [filters, setFilters] = useState({
    searchText: "",
    isFeatured: "",
    featuredHome: "",
    eventFrom: null,
    eventTo: null,
    status: "",
  });

  const hasActiveFilters = Boolean(
    filters.eventFrom ||
    filters.eventTo ||
    filters.status ||
    (sortBy && sortBy !== "default"),
  );

  useEffect(() => {
    if (pageAccessData) {
      if (!pageAccessData.viewAccess) {
        navigate("/404-error-page");
      } else {
        setPageAccessDetails(pageAccessData);
      }
    }
  }, [pageAccessData, navigate]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const payload = {
        pageNumber: currentPage,
        pageSize: entriesPerPage,
        searchText: filters.searchText || "",
        featuredHome:
          filters.featuredHome === "" ? null : filters.featuredHome === "true",
        isFeatured:
          filters.isFeatured === "" ? null : filters.isFeatured === "true",
        eventFrom: filters.eventFrom || null,
        eventTo: filters.eventTo || null,
        status: filters.status || "",
        sortColumn: "",
        sortDirection: "",
      };
      const response = await getExternalEventsFilterList(payload);
      const result = response.data?.result || {};
      const items = Array.isArray(result.items) ? result.items : [];
      setEventsList(items);
      setTotalCount(result.totalCount || 0);
    } catch (error) {
      handleErrors(error);
      setEventsList([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, entriesPerPage, filters]);

  const handleSearchClick = () => {
    setFilters((prev) => ({ ...prev, searchText: searchInput }));
    setCurrentPage(1);
  };

  const handleStatusFilterChange = (e) => {
    setFilters((prev) => ({ ...prev, status: e.target.value }));
    setCurrentPage(1);
  };

  const handleEventFromChange = (date) => {
    setFilters((prev) => ({ ...prev, eventFrom: date || null }));
    setCurrentPage(1);
  };

  const handleEventToChange = (date) => {
    setFilters((prev) => ({ ...prev, eventTo: date || null }));
    setCurrentPage(1);
  };

  const handleClearFilters = () => {
    setSearchInput("");
    setSortBy("default");
    setFilters({
      searchText: "",
      isFeatured: "",
      featuredHome: "",
      eventFrom: null,
      eventTo: null,
      status: "",
    });
    setCurrentPage(1);
  };

  const handlePublishToggle = async (item, checked) => {
    const eventId = getEventId(item);
    const currentStatus = resolveEventStatus(item);

    // Determine desired status based on toggle state and rules:
    // - Turning ON => Active
    // - Turning OFF => Inactive (unless current is Draft -> remain Draft)
    let targetStatus = null;
    if (checked) {
      targetStatus = EVENT_STATUS.ACTIVE;
    } else {
      if (currentStatus === EVENT_STATUS.DRAFT) {
        // Leave as Draft; do not convert to Inactive
        return;
      }
      targetStatus = EVENT_STATUS.INACTIVE;
    }

    setStatusChangingId(eventId);
    try {
      const response = await changeExternalEventStatus({
        id: eventId,
        status: targetStatus,
      });

      if (response?.data?.isSuccess === false) {
        toast.error(response.data.message || "Failed to update event status.");
        return;
      }

      setEventsList((prev) =>
        prev.map((row) =>
          getEventId(row) === eventId
            ? { ...row, status: targetStatus, Status: targetStatus }
            : row,
        ),
      );

      toast.success(
        targetStatus === EVENT_STATUS.ACTIVE
          ? "Event published successfully!"
          : "Event unpublished successfully!",
      );

      fetchData();
    } catch (error) {
      handleErrors(error);
    } finally {
      setStatusChangingId(null);
    }
  };

  const renderPublishToggle = (item) => {
    const eventId = getEventId(item);
    const status = resolveEventStatus(item);
    const isActive = status === EVENT_STATUS.ACTIVE;
    const isUpdating = statusChangingId === eventId;

    return (
      <div className="d-flex align-items-center">
        <div className="form-check form-switch">
          <input
            className="form-check-input"
            type="checkbox"
            role="switch"
            id={`event-switch-${eventId}`}
            checked={isActive}
            onChange={(e) => handlePublishToggle(item, e.target.checked)}
            disabled={!pageAccessDetails.editAccess || isUpdating}
          />
        </div>
        {isUpdating ? (
          <div
            className="spinner-border spinner-border-sm ms-2"
            role="status"
          />
        ) : null}
      </div>
    );
  };

  const sortedEvents = useMemo(() => {
    if (sortBy === "default") {
      return eventsList;
    }

    const list = [...eventsList];

    switch (sortBy) {
      case "name_asc":
        list.sort((a, b) => getEventName(a).localeCompare(getEventName(b)));
        break;
      case "name_desc":
        list.sort((a, b) => getEventName(b).localeCompare(getEventName(a)));
        break;
      case "event_desc":
        list.sort((a, b) =>
          compareTimestamps(
            toTimestamp(getEventsFromRaw(a)),
            toTimestamp(getEventsFromRaw(b)),
            "desc",
          ),
        );
        break;
      case "event_asc":
        list.sort((a, b) =>
          compareTimestamps(
            toTimestamp(getEventsFromRaw(a)),
            toTimestamp(getEventsFromRaw(b)),
            "asc",
          ),
        );
        break;
      case "added_desc":
        list.sort((a, b) =>
          compareTimestamps(
            toTimestamp(getAddedOnRaw(a)),
            toTimestamp(getAddedOnRaw(b)),
            "desc",
          ),
        );
        break;
      case "added_asc":
        list.sort((a, b) =>
          compareTimestamps(
            toTimestamp(getAddedOnRaw(a)),
            toTimestamp(getAddedOnRaw(b)),
            "asc",
          ),
        );
        break;
      default:
        break;
    }

    return list;
  }, [eventsList, sortBy]);

  const totalPages = Math.ceil(totalCount / entriesPerPage) || 0;

  const handleEntriesChange = (value) => {
    setEntriesPerPage(value);
    setCurrentPage(1);
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const handleDelete = async (eventId) => {
    const confirmed = await confirmDelete("Event");
    if (confirmed) {
      try {
        await deleteExternalEvent(eventId);
        Swal.fire(
          "Deleted!",
          "The event has been deleted successfully.",
          "success",
        );
        fetchData();
      } catch (error) {
        handleErrors(error);
      }
    }
  };

  const tableColumns = useMemo(() => {
    const columns = ["Event Name", "Event Date", "Status"];
    if (isSuperAdmin) {
      columns.push("Publish");
    }
    columns.push("Action");
    return columns;
  }, [isSuperAdmin]);

  const rowColumns = useMemo(() => {
    const columns = ["title", "eventDate", "status"];
    if (isSuperAdmin) {
      columns.push("publishToggle");
    }
    return columns;
  }, [isSuperAdmin]);

  return (
    <>
      <ComponentHeader title="Events" />

      {pageAccessDetails.viewAccess ? (
        <div className="row">
          <div className="col-xxl-12">
            <div className="card mt-xxl-n5 xc-manage-events">
              <div className="card-header d-flex flex-wrap align-items-center justify-content-between gap-2">
                <h5 className="mb-0">Manage Events</h5>
                {pageAccessDetails.addAccess ? (
                  <Link to="/events/add" className="btn btn-secondary btn-sm">
                    <i className="ri-add-line align-middle me-1"></i>
                    Add Event
                  </Link>
                ) : null}
              </div>
              <div className="card-body manage-amenity-master-card-body">
                <div className="xc-toolbar">
                  <EntriesDropdown
                    entriesPerPage={entriesPerPage}
                    onEntriesChange={handleEntriesChange}
                  />
                  <div className="xc-toolbar__right">
                    <div className="xc-table-search xc-quick-search">
                      <i
                        className="ri-search-line xc-table-search__icon xc-quick-search-icon"
                        onClick={handleSearchClick}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            handleSearchClick();
                          }
                        }}
                        aria-label="Search"
                      ></i>
                      <input
                        type="search"
                        id="eventSearchText"
                        className="form-control xc-table-search__input"
                        placeholder="Search events..."
                        value={searchInput}
                        onChange={(e) => setSearchInput(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            handleSearchClick();
                          }
                        }}
                        autoComplete="off"
                      />
                    </div>
                    <div className="xc-filter-popup">
                      <button
                        type="button"
                        className={`xc-filter-icon-btn ${
                          isFilterOpen ? "is-active" : ""
                        } ${hasActiveFilters ? "has-filters" : ""}`}
                        onClick={() => setIsFilterOpen(true)}
                        aria-expanded={isFilterOpen}
                        aria-haspopup="dialog"
                        title="Filter"
                        aria-label="Filter"
                      >
                        <i className="ri-filter-2-fill" aria-hidden="true" />
                        {hasActiveFilters ? (
                          <span className="xc-filter-icon-btn__dot" />
                        ) : null}
                      </button>
                    </div>
                  </div>
                </div>

                <Modal
                  show={isFilterOpen}
                  onHide={() => setIsFilterOpen(false)}
                  centered
                  className="xc-filter-modal"
                  dialogClassName="xc-filter-modal__dialog"
                  contentClassName="xc-filter-modal__content"
                  enforceFocus={false}
                >
                  <Modal.Header closeButton className="xc-filter-modal__header">
                    <div>
                      <Modal.Title as="h5">Filter events</Modal.Title>
                      <p className="xc-filter-modal__subtitle mb-0">
                        Refine events by date range, status, or sort order.
                      </p>
                    </div>
                  </Modal.Header>
                  <Modal.Body className="xc-filter-modal__body">
                    <div className="row g-3">
                      <div className="col-md-6">
                        <label htmlFor="eventFromFilter" className="form-label">
                          Event From
                        </label>
                        <Flatpickr
                          id="eventFromFilter"
                          className="form-control"
                          placeholder="Select date"
                          value={filters.eventFrom}
                          options={DATE_ONLY_PICKER_OPTIONS}
                          onChange={([date]) => handleEventFromChange(date)}
                        />
                      </div>
                      <div className="col-md-6">
                        <label htmlFor="eventToFilter" className="form-label">
                          Event To
                        </label>
                        <Flatpickr
                          id="eventToFilter"
                          className="form-control"
                          placeholder="Select date"
                          value={filters.eventTo}
                          options={DATE_ONLY_PICKER_OPTIONS}
                          onChange={([date]) => handleEventToChange(date)}
                        />
                      </div>
                      <div className="col-md-6">
                        <label
                          htmlFor="eventStatusFilter"
                          className="form-label"
                        >
                          Status
                        </label>
                        <select
                          id="eventStatusFilter"
                          className="form-select"
                          value={filters.status}
                          onChange={handleStatusFilterChange}
                        >
                          {STATUS_FILTER_OPTIONS.map((opt) => (
                            <option key={opt.value} value={opt.value}>
                              {opt.label}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="col-md-6">
                        <label htmlFor="eventSortBy" className="form-label">
                          Sort By
                        </label>
                        <select
                          id="eventSortBy"
                          className="form-select"
                          value={sortBy}
                          onChange={(e) => setSortBy(e.target.value)}
                        >
                          {SORT_OPTIONS.map((opt) => (
                            <option key={opt.value} value={opt.value}>
                              {opt.label}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </Modal.Body>
                  <Modal.Footer className="xc-filter-modal__footer">
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      onClick={handleClearFilters}
                    >
                      Clear
                    </button>
                    <div className="xc-filter-modal__footer-right">
                      <button
                        type="button"
                        className="btn btn-outline-secondary"
                        onClick={() => setIsFilterOpen(false)}
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={() => setIsFilterOpen(false)}
                      >
                        Apply filters
                      </button>
                    </div>
                  </Modal.Footer>
                </Modal>

                {loading ? (
                  <TableSkeleton
                    columns={tableColumns}
                    rows={entriesPerPage > 10 ? 10 : entriesPerPage}
                  />
                ) : (
                  <div className="table-responsive">
                    <table className="table align-middle table-bordered">
                      <TableHeader columns={tableColumns} />
                      <tbody className="manage-page-group-table-values">
                        {sortedEvents.length === 0 ? (
                          <TableDataStatusError
                            colspan={String(tableColumns.length)}
                          />
                        ) : (
                          sortedEvents.map((item) => {
                            const rowData = {
                              title: renderEventCell(item),
                              eventDate: renderEventDateCell(item),
                              status: renderEventStatusBadgeForItem(item),
                            };

                            if (isSuperAdmin) {
                              rowData.publishToggle = renderPublishToggle(item);
                            }

                            return (
                              <TablesRow
                                key={getEventId(item)}
                                rowData={rowData}
                                columns={rowColumns}
                                hideIcons={false}
                                onEdit={() => {
                                  navigate(
                                    `/events/update/${getEventId(item)}`,
                                  );
                                }}
                                onDelete={() => handleDelete(getEventId(item))}
                                pageLevelAccessData={pageAccessDetails}
                              />
                            );
                          })
                        )}
                      </tbody>
                    </table>
                  </div>
                )}

                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  totalEntries={totalCount}
                  entriesPerPage={entriesPerPage}
                  onPageChange={handlePageChange}
                />
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
};
